/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/jsx-boolean-value */
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { TextField, Dialog, Card } from '@material-ui/core';
import SelectableCard from 'src/components/SelectableCard';
import { Button, Heading, Body } from 'src/components/UI';
import {
    useRouter,
    useLocalStorage,
    LOCAL_STORAGE_KEYS,
    useEventsReporter,
    eventMetricsMap,
} from 'src/hooks';
import { REWARDS_ENROLLMENT_ROUTES } from 'src/routers/routes';
import { mergeClasses } from 'src/stylesheets/utils';
import {
    page_personalize_points_card1_body,
    page_personalize_points_card1_title,
    page_personalize_points_card2_body,
    page_personalize_points_card2_title,
    page_personalize_points_card3_title,
    page_personalize_points_card3_body,
    page_personalize_points_title_part1,
    page_personalize_points_title_part2_bold,
    page_personalize_points_title_part3,
    page_personalize_points_subtitle,
    nouns_rewards_enrollment_bottom_right_button,
} from 'src/utils/stringConstants';
import {
    IconBriefcaseWithPurpleGradient,
    IconPencilRulerWithPurpleGradient,
    IconUserProfileWithPurpleGradient,
} from 'src/components/Icons';
import { updateUserAccountType } from 'src/services/userServices';
import { REWARDS_ENROLLMENT_TRACKER } from '../types';
import styles from './styles.module.scss';

function PersonalizePointsScreen() {
    const [organizationName, setOrganizationNamee] = useState('');
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [customerType, setCustomerType] = useState<
        'consumer' | 'business' | 'teacher' | null
    >(null);
    const { push } = useRouter();
    const { analyticsScreenCustomNames, analyticsCustomEvent } =
        useEventsReporter();
    const {
        mutateAsync: mutateUserAccountType,
        isLoading: isMutatingUserAccountType,
    } = updateUserAccountType();
    const [
        { completedEnrollmentSteps, skippedEnrollmentSteps },
        setEnrollmentSteps,
    ] = useLocalStorage<REWARDS_ENROLLMENT_TRACKER>(
        LOCAL_STORAGE_KEYS.ENROLLMENT_TRACKER,
        {
            completedEnrollmentSteps: {},
            skippedEnrollmentSteps: {},
        }
    );
    const cards = [
        {
            value: 'consumer',
            heading: page_personalize_points_card1_title,
            body: page_personalize_points_card1_body,
        },
        {
            value: 'business',
            heading: page_personalize_points_card2_title,
            body: page_personalize_points_card2_body,
        },
        {
            value: 'teacher',
            heading: page_personalize_points_card3_title,
            body: page_personalize_points_card3_body,
        },
    ];

    const getClickText: any = {
        consumer: 'Myself',
        business: 'Business',
        teacher: 'School',
    };

    const CardIcon = useCallback(
        ({ cardValue }: { cardValue: typeof customerType }) => {
            switch (cardValue) {
                case 'consumer':
                    return <IconUserProfileWithPurpleGradient />;
                case 'business':
                    return <IconBriefcaseWithPurpleGradient />;
                case 'teacher':
                    return <IconPencilRulerWithPurpleGradient />;
                default:
                    return null;
            }
        },
        []
    );

    useLayoutEffect(() => {
        if (completedEnrollmentSteps[REWARDS_ENROLLMENT_ROUTES.peronalize]) {
            push(REWARDS_ENROLLMENT_ROUTES.finish);
        }
    }, []);

    return (
        <>
            <div className={styles.container}>
                <div>
                    <div className={styles.py4}>
                        <div aria-label="Lets's personalize how you earn points">
                            <Heading
                                type="h3"
                                color="textBlack"
                                size={30}
                                weight="light"
                                lineHeight={34}
                                ariaHidden={true}
                            >
                                {page_personalize_points_title_part1}
                                <Body
                                    display="inline"
                                    size={30}
                                    color="textPurple"
                                    weight="bold"
                                >
                                    {page_personalize_points_title_part2_bold}
                                </Body>
                                {page_personalize_points_title_part3}
                            </Heading>
                        </div>
                        <Heading
                            type="h6"
                            size={16}
                            weight="light"
                            className={styles.subheading}
                        >
                            {page_personalize_points_subtitle}
                        </Heading>
                        <div className={styles.cardGroupContainer}>
                            {cards.map((card) => (
                                <SelectableCard
                                    key={card.value}
                                    selected={customerType === card.value}
                                    onSelect={() =>
                                        setCustomerType(
                                            card.value as typeof customerType
                                        )
                                    }
                                    containerClassname={styles.cardContainer}
                                    withInput={
                                        customerType === card.value &&
                                        customerType === 'business' && (
                                            <TextField
                                                classes={{
                                                    root: styles.textfieldUnderline,
                                                }}
                                                className={styles.mt4}
                                                variant="standard"
                                                placeholder="Company name *"
                                                value={organizationName}
                                                onChange={(e) =>
                                                    setOrganizationNamee(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        )
                                    }
                                >
                                    <div
                                        className={styles.cardHeading}
                                        aria-label={card.heading}
                                    >
                                        <CardIcon
                                            cardValue={
                                                card.value as typeof customerType
                                            }
                                        />
                                        <Heading
                                            type="h6"
                                            size={20}
                                            weight="bold"
                                            color="textBlack"
                                            ariaHidden={true}
                                        >
                                            {card.heading}
                                        </Heading>
                                    </div>
                                    <Body
                                        weight="light"
                                        size={16}
                                        ariaDescription={card.body}
                                    >
                                        {card.body}
                                    </Body>
                                </SelectableCard>
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <Button
                        fullWidth
                        disabled={
                            !customerType ||
                            (customerType === 'business' && !organizationName)
                        }
                        loading={isMutatingUserAccountType}
                        onClick={() => {
                            if (customerType) {
                                mutateUserAccountType({
                                    customerType,
                                    organizationName,
                                })
                                    .then(() => {
                                        analyticsCustomEvent(
                                            eventMetricsMap.easy_rewards_enroll,
                                            {
                                                click_text:
                                                    getClickText[customerType],
                                                element_location:
                                                    'personalize rewards',
                                            }
                                        );
                                        delete skippedEnrollmentSteps[
                                            REWARDS_ENROLLMENT_ROUTES.peronalize
                                        ];
                                        setEnrollmentSteps({
                                            skippedEnrollmentSteps: {
                                                ...skippedEnrollmentSteps,
                                            },
                                            completedEnrollmentSteps: {
                                                ...completedEnrollmentSteps,
                                                [REWARDS_ENROLLMENT_ROUTES.peronalize]:
                                                    true,
                                            },
                                        });
                                        push(REWARDS_ENROLLMENT_ROUTES.finish);
                                        analyticsScreenCustomNames({
                                            user_type: customerType,
                                        });
                                    })
                                    .catch(() => setShowErrorDialog(true));
                            }
                        }}
                    >
                        Continue
                    </Button>
                </div>
            </div>
            <Dialog open={showErrorDialog}>
                <div
                    className={mergeClasses(
                        styles.p4,
                        styles.flex,
                        styles.flexCol,
                        styles.rowGap4,
                        styles.alignCenter
                    )}
                >
                    <div>
                        <Heading type="h3" weight="bold">
                            Sorry, we are not able to save right now.
                        </Heading>
                        <Body>
                            Try confirming your personalization again or skip
                            for now.
                        </Body>
                    </div>
                    <Button
                        bgColor="bgBlack"
                        onClick={() => setShowErrorDialog(false)}
                    >
                        Got it
                    </Button>
                </div>
            </Dialog>
        </>
    );
}

export default PersonalizePointsScreen;
